"use client"

import { Icon } from "@/components/ui/icon"
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
  type ToastProps,
} from "@/components/ui/toast"
import { useToast } from "@/components/ui/use-toast"

type ToastVariant = ToastProps["variant"]

// Map MD3 snackbar variant to its leading status icon. Surface stays neutral
// `surface-1`; the icon shape plus the slim left accent border carry the tone.
function getLeadingIconName(variant: ToastVariant) {
  if (variant === "success") {
    return "lucide:circle-check"
  }
  if (variant === "error") {
    return "lucide:circle-alert"
  }
  if (variant === "warning") {
    return "lucide:triangle-alert"
  }
  if (variant === "info") {
    return "lucide:info"
  }
  return null
}

function getLeadingIconColorClass(variant: ToastVariant) {
  if (variant === "success") {
    return "text-success"
  }
  if (variant === "error") {
    return "text-error"
  }
  if (variant === "warning") {
    return "text-warning"
  }
  if (variant === "info") {
    return "text-info"
  }
  return "text-on-surface-variant"
}

// MD3 snackbar defaults: 5s without an action, 8s when the user must reach
// for an action button before it dismisses.
const TOAST_DURATION_DEFAULT_MS = 5000
const TOAST_DURATION_WITH_ACTION_MS = 8000

export function Toaster() {
  const { toasts } = useToast()

  return (
    <ToastProvider>
      {toasts.map(function ({ id, title, description, action, variant, duration, ...props }) {
        const leadingIcon = getLeadingIconName(variant)
        const resolvedDuration =
          duration !== undefined
            ? duration
            : action !== undefined
              ? TOAST_DURATION_WITH_ACTION_MS
              : TOAST_DURATION_DEFAULT_MS
        return (
          <Toast key={id} variant={variant} duration={resolvedDuration} {...props}>
            {leadingIcon !== null && (
              <Icon
                icon={leadingIcon}
                size={20}
                className={`mt-0.5 shrink-0 self-start ${getLeadingIconColorClass(variant)}`}
              />
            )}
            <div className="min-w-0 flex-1">
              {title !== undefined && title !== null && <ToastTitle>{title}</ToastTitle>}
              {description !== undefined && description !== null && (
                <ToastDescription>{description}</ToastDescription>
              )}
            </div>
            {action}
            <ToastClose />
          </Toast>
        )
      })}
      <ToastViewport />
    </ToastProvider>
  )
}
